import * as Sentry from '@sentry/react';

// Some browsers (notably Safari on iOS with a detached <video>) never fire loadedmetadata
// nor error. Without a deadline the upload would wait forever after reaching 100%.
export const VIDEO_METADATA_TIMEOUT_MS = 10000;

const toMegabytes = (bytes) => (Number.isFinite(bytes) ? parseFloat((bytes / 1048576).toFixed(2)) : 0);

const reportUnreadableDuration = (file, reason, sizeMb) => {
  try {
    const name = typeof file?.name === 'string' ? file.name : '';
    Sentry.captureMessage('Video duration could not be read in the browser', {
      level: 'warning',
      tags: { feature: 'video-upload', reason },
      extra: {
        extension: name.includes('.') ? name.split('.').pop().toLowerCase() : '',
        mime_type: file?.type || '',
        size_mb: sizeMb,
      },
    });
  } catch (_) {
    // Reporting must never break an upload.
  }
};

/**
 * Size (MB) and duration (s) of a local video file, read from its metadata by the browser.
 * Always resolves: when the duration cannot be read (error, timeout, NaN/Infinity) it resolves
 * with video_duration 0 and reports the case to Sentry.
 */
export const getSizeAndDurationByVideoFile = (videoFile, { timeoutMs = VIDEO_METADATA_TIMEOUT_MS } = {}) =>
  new Promise((resolve) => {
    const video_size_without_compress = toMegabytes(videoFile?.size);
    const video = document.createElement('video');
    let objectUrl = null;
    let timer = null;
    let settled = false;

    const finish = (video_duration, failureReason = null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      video.onloadedmetadata = null;
      video.onerror = null;
      video.removeAttribute('src');
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      if (failureReason) reportUnreadableDuration(videoFile, failureReason, video_size_without_compress);
      resolve({ video_duration, video_size_without_compress });
    };

    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      const { duration } = video;
      if (Number.isFinite(duration) && duration > 0) {
        finish(Math.round(duration));
      } else {
        finish(0, 'non-finite');
      }
    };
    video.onerror = () => finish(0, 'error');
    timer = setTimeout(() => finish(0, 'timeout'), timeoutMs);

    try {
      objectUrl = URL.createObjectURL(videoFile);
      video.src = objectUrl;
    } catch (_) {
      finish(0, 'invalid-file');
    }
  });
